import WboDrawingAdapter
    from '@/components/common/ed-board/ed-board-tools/wbo-client/interfaces/wbo-drawing-adapter.interface';
import {
    WboAnnotationData, WboBatchedBroadcastMsg, WboDelta,
    WboMsg
} from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-annotation.model';
import GenericQueue, {
    GenericQueueProcessor
} from '@/components/common/ed-board/ed-board-tools/wbo-client/util/generic-queue';
import EdWboDrawer from '@/components/common/ed-board/ed-board-tools/ed-wbo-drawer/ed-wbo-drawer';

/**
 * A WboDrawingAdapter implementation that adapts an EdWboDrawer to interact with WboClient.
 */
export default class WboDefaultDrawingAdapter implements WboDrawingAdapter {
    private readonly drawnAnnotationsQueue: GenericQueue<WboDelta>; // This is the output queue of this adapter
    private readonly transientEventsQueue: GenericQueue<WboMsg>;
    private readonly annotationsToDrawQueue: GenericQueue<WboMsg>; // This is the input queue of this adapter. This receives messages both from the WboClient and from the drawer engine.
    private drawerEngine: EdWboDrawer;
    private readonly boardName: string;

    constructor(drawerEngine: EdWboDrawer, boardName: string) {
        this.boardName = boardName;
        this.drawerEngine = drawerEngine;
        this.drawnAnnotationsQueue = new GenericQueue<WboDelta>();
        this.annotationsToDrawQueue = new GenericQueue<WboMsg>();
        this.transientEventsQueue = new GenericQueue<WboMsg>();

        this.startListeningToInputQueue();
        this.startListeningToDrawerActions();
    }

    clear(): void {
        this.drawerEngine.clearDrawings();
    }

    /**
     * Continually listen to elements in the incoming queue.
     */
    private startListeningToInputQueue() {
        new GenericQueueProcessor(this.annotationsToDrawQueue)
            .runOnEachItem(async (msg: WboMsg) => {
                this.process(msg);
            })
            .start();
    }

    /**
     * Register a callback function to be called whenever the drawer engine processes the user input.
     */
    private startListeningToDrawerActions() {
        this.drawerEngine.onDraw((tool: any, data: any) => {
            if (tool?.name === 'Cursor') {
                this.transientEventsQueue.enqueue({
                    board: this.boardName,
                    clientId: '1',
                    operationId: 1,
                    data: {...data, tool: tool.name}
                });
            }
            else if (tool?.name === 'Eraser All') {
                this.drawerEngine.removeAllCurrentUserAnnotations();
                this.drawnAnnotationsQueue.enqueue({
                    msg: {
                        board: this.boardName,
                        clientId: '1', // This is a dummy value, it will be replaced when message is sent
                        operationId: 1, // This is a dummy value, it will be replaced when message is sent
                    }, opType: 'removeAllByUser'
                });
            }
            else {
                this.drawnAnnotationsQueue.enqueue({
                    msg: {
                        board: this.boardName,
                        clientId: '1', // This is a dummy value, it will be replaced when message is sent
                        operationId: 1, // This is a dummy value, it will be replaced when message is sent
                        data: {...data, tool: tool.name}},
                    opType: 'broadcast'
                });
            }
        });
    }

    /**
     * Draws the given data. Idempotency is expected to be handled by the concrete drawer engine.
     */
    async process(msg: WboMsg): Promise<void> {        
        if (msg.elementsToDelete?.length > 0) {
            return this.drawerEngine.removeAnnotationsFromMessage(msg);
        }
        return this.draw(msg.data);
    }

    /**
     * Draws the given data. The underlying drawing engine (WboDrawingAdapter) must be responsible for properly
     * handling the idempotence of drawing operations.
     */
    async draw(annotationData: WboAnnotationData): Promise<void> {
        const data = structuredClone(annotationData ?? {} as WboAnnotationData);
        if (data.type === 'child') {
            data.tool = 'Pencil';
        }
        if (data.tool === 'Cursor' && !this.drawerEngine.settings.showOtherCursors) {
            return;
        }
        return this.drawerEngine.handleMessage(data);
    }

    batchDraw(_children: WboAnnotationData[]) {
        this.draw({_children, tool: '', type: ''});
    }

    /**
     * Gets the queue where the adapter will listen for new drawings to be drawn in the drawing engine.
     */
    getDrawingsInputQueue(): GenericQueue<WboMsg> {
        return this.annotationsToDrawQueue;
    }

    /**
     * Gets the queue where the adapter will publish the drawings that have been drawn by the user in the drawing engine.
     */
    getDrawingsOutputQueue(): GenericQueue<WboDelta> {
        return this.drawnAnnotationsQueue;
    }

    /**
     * Gets the  queue of events produces by the drawing engine that are transient by nature (e.g. cursor movements).
     */
    getTransientEventsQueue(): GenericQueue<WboMsg> {
        return this.transientEventsQueue;
    }

    removeDrawing(id: string, username: string) {
        return this.drawerEngine.removeClientElements([id], username);
    }

    dispose() {
        this.drawerEngine.dispose();
    }

    bringToFront(annotationId: string): void {
        this.drawerEngine.bringToFront(annotationId);
    }

}
